import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import toast from 'react-hot-toast';
import {
  X,
  Search,
  LayoutDashboard,
  Table2,
  Plus,
  BarChart3
} from 'lucide-react';

const ComponentLibrary = ({ isOpen, onClose, onAddComponent }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (isOpen) {
      loadComponents();
    }
  }, [isOpen]);

  const loadComponents = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch('/api/visualization-components/library', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load components');
      }

      const data = await response.json();
      setComponents(data.data || []);
    } catch (error) {
      console.error('Error loading components:', error);
      toast.error('Failed to load visualization library');
    } finally {
      setLoading(false);
    }
  };

  const filteredComponents = components.filter(comp => {
    const matchesSearch = comp.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         comp.Description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || comp.ComponentType === filterType;
    return matchesSearch && matchesType;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'StatCard':
        return <LayoutDashboard className="w-5 h-5" />;
      case 'Table':
        return <Table2 className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Component Library
              </h3>
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search components..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="all">All Types</option>
                <option value="StatCard">Stat Cards</option>
                <option value="Table">Tables</option>
                <option value="BarChart">Bar Charts</option>
                <option value="LineChart">Line Charts</option>
                <option value="PieChart">Pie Charts</option>
              </select>
            </div>
          </div>

          {/* Component Grid */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : filteredComponents.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm || filterType !== 'all' 
                    ? 'No components match your search'
                    : 'No visualization components available yet'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredComponents.map(component => (
                  <div
                    key={component.ComponentID}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg text-purple-600 dark:text-purple-400">
                          {getTypeIcon(component.ComponentType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {component.Name}
                          </h4>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {component.ComponentType} • {component.EntityName}
                            {component.config?.gridSize && (
                              <span className="ml-2 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded text-xs">
                                {component.config.gridSize.width}×{component.config.gridSize.height}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => onAddComponent(component)}
                        className="btn btn-primary p-2 flex-shrink-0"
                        title="Add to dashboard"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    {component.Description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {component.Description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex justify-end">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentLibrary;

